import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  useColorModeValue,
  IconButton,
  Image,
  Center
} from '@chakra-ui/react';
import { FiArrowLeft, FiPlus, FiMoreVertical, FiPlay } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { getContent } from '../services/contentService';

interface Episode {
  id: string;
  title: string;
  episodeNumber: string;
  thumbnail?: string;
  duration: string;
  description?: string;
}

const EpisodesManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { seriesId, seasonId } = useParams<{ seriesId: string; seasonId: string }>();
  const toast = useToast();
  
  const [series, setSeries] = useState<any>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const loadSeries = useCallback(async () => {
    try {
      const seriesData = await getContent('series', 1);
      const foundSeries = seriesData.find(s => s.id === seriesId);
      if (foundSeries) {
        setSeries(foundSeries);
      }
    } catch (error) {
      console.error('Error loading series:', error);
    }
  }, [seriesId]);

  const loadEpisodes = useCallback(async () => {
    try {
      setLoading(true);
      // Load episodes from localStorage
      const savedEpisodes = localStorage.getItem(`episodes-${seriesId}`);
      if (savedEpisodes) {
        const episodesData = JSON.parse(savedEpisodes);
        // Filter episodes for the current season
        const seasonEpisodes = episodesData.filter((ep: any) => ep.seasonId === seasonId);
        setEpisodes(seasonEpisodes);
      } else {
        setEpisodes([]);
      }
    } catch (error) {
      console.error('Error loading episodes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load episodes.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [seriesId, seasonId, toast]);

  useEffect(() => {
    if (seriesId) {
      loadSeries();
      loadEpisodes();
    }
  }, [seriesId, seasonId, loadSeries, loadEpisodes]);

  const handleAddEpisode = () => {
    // Navigate to upload episode page
    navigate(`/series/${seriesId}/season/${seasonId}/upload-episode`);
  };

  // Function to refresh episodes (called when episodes are added)
  const refreshEpisodes = useCallback(() => {
    loadEpisodes();
  }, [loadEpisodes]);

  // Expose refresh function globally
  React.useEffect(() => {
    (window as any).refreshEpisodes = refreshEpisodes;
    return () => {
      delete (window as any).refreshEpisodes;
    };
  }, [refreshEpisodes]);

  const handleEpisodeClick = (episodeId: string) => {
    // Store current series ID for episode detail page
    localStorage.setItem('currentSeriesId', seriesId || '');
    // Navigate to episode detail
    navigate(`/episode/${episodeId}`);
  };


  const handleEpisodeOptions = (episodeId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent episode click
    
    // Find the episode data (for future use if needed)
    // const episode = episodes.find(ep => ep.id === episodeId);
    
    // Show episode options menu
    const options = [
      { label: 'View Details', action: () => handleEpisodeClick(episodeId) },
      { label: 'Edit Episode', action: () => handleEditEpisode(episodeId) },
      { label: 'Delete Episode', action: () => handleDeleteEpisode(episodeId) }
    ];
    
    // For now, show a simple alert with options
    const choice = window.prompt(`Episode Options:\n1. View Details\n2. Edit Episode\n3. Delete Episode\n\nEnter choice (1-3):`);
    if (choice) {
      const optionIndex = parseInt(choice) - 1;
      if (optionIndex >= 0 && optionIndex < options.length) {
        options[optionIndex].action();
      }
    }
  };

  const handleEditEpisode = (episodeId: string) => {
    // Store current series ID for episode edit page
    localStorage.setItem('currentSeriesId', seriesId || '');
    // Navigate to edit episode page
    navigate(`/episode/${episodeId}/edit`);
  };

  const handleDeleteEpisode = (episodeId: string) => {
    if (window.confirm('Are you sure you want to delete this episode? This action cannot be undone.')) {
      // Delete episode from localStorage
      const savedEpisodes = localStorage.getItem(`episodes-${seriesId}`);
      if (savedEpisodes) {
        const episodesData = JSON.parse(savedEpisodes);
        const episodeToDelete = episodesData.find((ep: any) => ep.id === episodeId);
        const updatedEpisodes = episodesData.filter((ep: any) => ep.id !== episodeId);
        localStorage.setItem(`episodes-${seriesId}`, JSON.stringify(updatedEpisodes));
        
        // Update season episode count
        const seasonsKey = `seasons-${seriesId}`;
        const existingSeasons = JSON.parse(localStorage.getItem(seasonsKey) || '[]');
        const updatedSeasons = existingSeasons.map((season: any) => {
          if (season.id === episodeToDelete?.seasonId) {
            return { ...season, episodeCount: Math.max(0, season.episodeCount - 1) };
          }
          return season;
        });
        localStorage.setItem(seasonsKey, JSON.stringify(updatedSeasons));
        
        // Refresh episodes list
        loadEpisodes();
        
        // Refresh seasons if function is available
        if ((window as any).refreshSeasons) {
          (window as any).refreshSeasons();
        }
        
        toast({
          title: 'Episode deleted',
          description: 'The episode has been successfully deleted.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  if (!user) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Please log in to manage episodes.
          </Text>
          <Button onClick={() => navigate('/login')} colorScheme="purple">
            Log In
          </Button>
        </VStack>
      </Center>
    );
  }

  if (loading) {
    return (
      <Center h="50vh">
        <Text>Loading...</Text>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={4} borderBottom="1px solid" borderColor="gray.200">
        <HStack gap={4} align="center">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate(-1)}
            size="sm"
            bg="purple.100"
            _hover={{ bg: 'purple.200' }}
          />
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Episodes
          </Text>
          <Text fontSize="sm" color="gray.500" ml="auto">
            {episodes.length}/5
          </Text>
        </HStack>
      </Box>

      {/* Series and Season Title */}
      {series && (
        <Box p={4}>
          <Text fontSize="lg" fontWeight="bold" color={textColor}>
            {series.title} – Season 1
          </Text>
        </Box>
      )}

      {/* Main Content */}
      <Box p={6}>
        <VStack spacing={4} align="stretch">
          {/* Existing Episodes */}
          {episodes.map((episode) => (
            <Box
              key={episode.id}
              bg={cardBg}
              borderRadius="lg"
              p={4}
              border="1px solid"
              borderColor="gray.200"
              cursor="pointer"
              _hover={{ shadow: 'md' }}
              onClick={() => handleEpisodeClick(episode.id)}
            >
              <HStack gap={4} align="start">
                {/* Episode Thumbnail */}
                <Box position="relative" flex="0 0 120px">
                  <Image
                    src={episode.thumbnail || '/placeholder-thumbnail.jpg'}
                    alt={episode.title}
                    w="120px"
                    h="80px"
                    objectFit="cover"
                    borderRadius="md"
                    fallback={
                      <Box
                        w="120px"
                        h="80px"
                        bg="gray.200"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <IconWrapper icon={FiPlay} size={24} color="gray.400" />
                      </Box>
                    }
                  />
                  {/* Play Button Overlay */}
                  <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    bg="rgba(0,0,0,0.7)"
                    borderRadius="full"
                    p={2}
                  >
                    <IconWrapper icon={FiPlay} size={16} color="white" />
                  </Box>
                </Box>

                {/* Episode Details */}
                <VStack align="start" flex="1" gap={1}>
                  <HStack w="full" justify="space-between">
                    <Text fontSize="md" fontWeight="bold" color={textColor}>
                      {episode.title}
                    </Text>
                    <IconButton
                      aria-label="Episode options"
                      icon={<IconWrapper icon={FiMoreVertical} size={16} />}
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleEpisodeOptions(episode.id, e)}
                    />
                  </HStack>
                  
                  <Text fontSize="sm" color={labelColor}>
                    {episode.duration}
                  </Text>
                </VStack>
              </HStack>
            </Box>
          ))}

          {/* Add Episode Button */}
          <Button
            colorScheme="purple"
            variant="outline"
            size="lg"
            onClick={handleAddEpisode}
            leftIcon={<IconWrapper icon={FiPlus} size={20} />}
            w="100%"
            h="60px"
          >
            Add Episode
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default EpisodesManagement;
